import * as React from 'react';
import {
  Draggable,
  DraggableDragStartEvent,
  DraggableDragEvent,
  DraggableDragEndEvent,
  classNames
} from '@progress/kendo-react-common';
import PuzzleButton from '../../../UI/button/PuzzleButton';
import ConditionalField from '../ConditionalField';
import { Component } from '../../../../dispatcher/modules';
import { 
  CONDITIONAL_FILTER_DRAGGABLE_ITEM_PROPS, 
  CONDITIONAL_FILTER_DRAGGABLE_ITEM_STATE 
} from './Types';
import Container from '../../../layout/container/Container';

/**
 * Draggable item
 * Creates a draggable item for condition field so that we can create a drag and drop to filters component
 */
export class DraggableItem extends Component<CONDITIONAL_FILTER_DRAGGABLE_ITEM_PROPS, CONDITIONAL_FILTER_DRAGGABLE_ITEM_STATE> {
  /**
   * Draggable element ref
   * @private
   */
  private dragEl: React.RefObject<HTMLDivElement | null> = React.createRef();

  /**
   * Drag handle ref
   * @private
   */
  private readonly dragHandle;

  /**
   * Drag z-index property
   * @private
   */
  private readonly dragZIndex: number = 10;

  constructor(props) {
    super(props);
    this.dragHandle = React.createRef();

    this.state = {
      ...this.state,
      ...props,
      dragged: false,
      initial: null
    };
  }

  componentDidUpdate(prevProps: Readonly<CONDITIONAL_FILTER_DRAGGABLE_ITEM_PROPS>, prevState: Readonly<CONDITIONAL_FILTER_DRAGGABLE_ITEM_STATE>) {
    if (prevState.dragged !== this.state.dragged) {
      this.props.setDragging(this.state.dragged);
    }
  }

  /**
   * Sets drag state to dragged
   * Checks if drag handle is dragged and not some other part of the item
   * It also sets the initial coordinates for the item
   * @param {DraggableDragStartEvent} event
   */
  handleDragStart = ({ event }: DraggableDragStartEvent) => {
    // @ts-ignore
    if (!event.originalEvent.target.classList.contains('tp-handle')) {
      return;
    }

    this.dragEl.current.style.zIndex = (this.dragZIndex * 2).toString();
    this.setState({
      dragged: true,
      initial: { x: event.clientX, y: event.clientY }
    });

    this.props.setDropIndex(this.props.index);
  }

  /**
   * Recalculates new coordinates and sets the item to a new position
   * @param {DraggableDragEvent} event
   */
  handleDrag = ({ event }: DraggableDragEvent) => {
    if (!this.dragEl.current || !this.state.initial) {
      return;
    }

    const transform = `translate(${event.clientX - this.state.initial.x}px, ${
      event.clientY - this.state.initial.y
    }px)`;

    this.dragEl.current.style.transition = 'none';
    this.dragEl.current.style.transform = transform;
  }

  /**
   * Ends the drag and sets the drag state to false
   * @param {DraggableDragEndEvent} e
   */
  handleDragEnd = (e: DraggableDragEndEvent) => {
    if (!this.dragEl.current) {
      return;
    }

    this.dragEl.current.style.transform = null;
    this.dragEl.current.style.zIndex = this.dragZIndex.toString();

    this.setState({
      dragged: false,
      initial: null
    });
  }

  renderBody() {
    let { dragged } = this.state;

    const itemCls = classNames(
      'tp-draggable-item',
      { 'drag': dragged }
    );

    return (
      <React.Fragment>
        <div className={itemCls}>
          <Draggable
            childRef={this.dragEl}
            onDragStart={this.handleDragStart}
            onDrag={this.handleDrag}
            onDragEnd={this.handleDragEnd}
          >
            <Container
              hBox
              alignItemsCenter
              ref={this.dragHandle}
              gap={1}
              className={'tp-draggable-handle'}
            >
              <i
                className={'tp-draggable-icon tp-icon tp-icon-md tp-handle tp-color-blue-500'}
                aria-hidden={'true'}
              />
              <ConditionalField
                filterId={`conditional-field-${this.props.index}`}
                data={this.state.data}
                fieldData={this.props.fieldData}
                onChange={this.props.onFilterChange.bind(this, this.props.index)}
                {...this.props}
              />
              <PuzzleButton
                onClick={this.props.removeField.bind(this, this.props.index)}
                iconClass={'tp-icon tp-delete-can'}
                size={'small'}
                fillMode={'flat'}
              />
            </Container>
          </Draggable>
        </div>
      </React.Fragment>
    );
  }
}
